/**
 * 設定 → 月次設定(2026-09-24 発注者指示「設定はメニューバーの設定から各項目を設定できるように」)。
 * 月ごとの前提値(目標原価率・通話料単価・管理者報酬の率・タイミー手数料・会社別アポ数目標)をここで入れる。
 * PL 画面の「月次設定」ボタンからも同じ入力を開ける(中身は `MonthSettings.tsx` の共通部品)。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@core/components/Toast';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import FilterBar from '@core/components/ui/FilterBar';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import { todayStr } from '@core/lib/date';
import type { Company, MonthStr, MonthTargets } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { addMonths } from '@core/dashboard/calc';
import { fetchMonthTargets } from '@core/dashboard/dashboardRepo';
import { MonthSettingsForm } from '@core/dashboard/MonthSettings';

export default function SettingsMonthlyPage() {
  const toast = useToast();
  const [month, setMonth] = useState<MonthStr>(todayStr().slice(0, 7) as MonthStr);
  const [targets, setTargets] = useState<MonthTargets | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, cs] = await Promise.all([fetchMonthTargets(month), fetchCompanies()]);
      setTargets(t);
      setCompanies(cs);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(() => companies.filter((c) => c.status === 'active'), [companies]);
  const monthLabel = `${month.slice(0, 4)}年${Number(month.slice(5))}月`;

  return (
    <div className="screen full">
      <PageHeader title="月次設定" meta={monthLabel} />
      <FilterBar label="対象の月">
        <span className="ui-date-nav">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, -1) as MonthStr)} aria-label="前の月">
            <IconChevronLeft size={16} />
          </button>
          <input type="month" className="ui-input" aria-label="月" value={month} onChange={(e) => e.target.value && setMonth(e.target.value as MonthStr)} />
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, 1) as MonthStr)} aria-label="次の月">
            <IconChevronRight size={16} />
          </button>
        </span>
        <span className="ui-period-label">{monthLabel}</span>
      </FilterBar>

      <Panel
        title={`月次設定(${monthLabel})`}
        note="**月ごとの値**です。原価率は「これ以上で赤」の基準で、日次【集計】・会社別・出勤形態別・PL・分析のすべてに効きます(既定 給湯器45% / 不用品50%)"
      >
        {loading ? <div className="ui-empty">読み込み中…</div> : targets ? <MonthSettingsForm month={month} targets={targets} companies={active} onSaved={load} /> : <div className="ui-empty">設定を読み込めませんでした</div>}
      </Panel>
    </div>
  );
}
